import type { CodingResult, GroupedCodingResult } from 'src/types/dashboard';

const toGroupedCodingResult = (
  codingResult: CodingResult,
): GroupedCodingResult[] =>
  codingResult.topics.reduce((acc: GroupedCodingResult[], subtopic) => {
    let existingSubtopic = acc.find(
      ({ label }) => label === subtopic.topicLabel,
    );
    if (!existingSubtopic) {
      const accLength = acc.push({
        label: subtopic.topicLabel,
        negativeCount: 0,
        neutralCount: 0,
        positiveCount: 0,
        negativeId: null,
        neutralId: null,
        positiveId: null,
        relevanceAll: 0,
        relevanceNegative: 0,
        relevanceNeutral: 0,
        relevancePositive: 0,
        totalOfAllCounts: 0,
        topicLabel: codingResult.categoryLabel,
      });
      existingSubtopic = acc[accLength - 1];
    }

    // If a selected subtopic exists (i.e. matches the label of an existing subtopic), the existing
    // topic's respective sentiment counts should match those of the corresponding selected subtopic
    switch (subtopic.sentimentLabel) {
      case 'negative': {
        existingSubtopic.negativeCount = subtopic.count;
        existingSubtopic.negativeId = subtopic.id;
        break;
      }
      case 'neutral': {
        existingSubtopic.neutralCount = subtopic.count;
        existingSubtopic.neutralId = subtopic.id;
        break;
      }
      case 'positive': {
        existingSubtopic.positiveCount = subtopic.count;
        existingSubtopic.positiveId = subtopic.id;
        break;
      }
    }

    const { negativeCount, neutralCount, positiveCount } = existingSubtopic;
    existingSubtopic.totalOfAllCounts =
      negativeCount + neutralCount + positiveCount;

    const { relevanceAll } = existingSubtopic;
    // If a selected subtopic exists (i.e. matches the label of an existing subtopic), get the
    // existing subtopic's sentiment totalOfAllCounts. If this is greater than the selected
    // subtopic's relevance, it should be adopted as the subtopic's new overall relevance.
    const newRelevanceAll =
      relevanceAll > subtopic.relevance ? relevanceAll : subtopic.relevance;
    existingSubtopic.relevanceAll =
      existingSubtopic.relevanceNegative =
      existingSubtopic.relevanceNeutral =
      existingSubtopic.relevancePositive =
        newRelevanceAll;

    return acc;
  }, []);

/**
 * Group coding results.
 * Note: sum of topic count sentiments is not expected to equal the count_* of the corresponding category.
 * Accordingly, display of topic results is not expected to sum up to the given count_* / totalOfAllCounts values.
 * The "codingResults" required for this come from the reporting engine and are therefore not customisable.
 */
export const groupCalculatedCodingResults = (
  codingResults: CodingResult[],
): GroupedCodingResult[] =>
  codingResults.map((codingResult) => ({
    label: codingResult.categoryLabel,
    negativeCount: codingResult.countNegative,
    neutralCount: codingResult.countNeutral,
    positiveCount: codingResult.countPositive,
    negativeId: null,
    positiveId: null,
    neutralId: null,
    relevanceAll: codingResult.relevanceAll,
    relevanceNegative: codingResult.relevanceNegative,
    relevanceNeutral: codingResult.relevanceNeutral,
    relevancePositive: codingResult.relevancePositive,
    totalOfAllCounts:
      codingResult.countNegative +
      codingResult.countNeutral +
      codingResult.countPositive,
    subTopics: toGroupedCodingResult(codingResult),
  }));
